import { useEffect, useRef } from 'react'
import { OpenNew } from '@/assets/icons'
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from '@/components/popover/Popover'
import { CaseStatusCheckedLabel, type CaseStatusCheckedLabelSize } from '@/citator/components/CaseStatusCheckedLabel'
import { CaseStatusTag } from '@/citator/components/CaseStatusTag'
import { DisplayableCaseStatus, isCheckedCaseStatus } from '@/citator/schema/case-status-schema'
import { kCaseStatusDefinitions } from '@/citator/utils/case-status-definitions'
import clsx from 'clsx'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { CitatorSurface, mapCaseStatusToBadgeType } from '@/analytics/enums/citator.enums'
import { trackCitatorBadgeClicked, trackCitatorBadgeImpression } from '@/analytics/utils/citator-analytics'

type CaseStatusInfoTagProps = {
  status: DisplayableCaseStatus
  onViewCitationAnalysis?: () => void
  className?: string
  checkedLabel?: string
  checkedLabelSize?: CaseStatusCheckedLabelSize
  // Analytics-only: when omitted, badge events no-op. `surface` enables both the
  // impression and the click; set `trackImpression={false}` where the impression
  // is tracked elsewhere (e.g. the reference list, via CitatorReferenceGroupExpanded).
  surface?: CitatorSurface
  trackImpression?: boolean
}

export function CaseStatusInfoTag({
  status,
  onViewCitationAnalysis,
  className,
  checkedLabel,
  checkedLabelSize = 'compact',
  surface,
  trackImpression = true,
}: CaseStatusInfoTagProps) {
  const { heading, body } = kCaseStatusDefinitions[status]
  const { trackEvent } = useAnalytics()
  const badgeType = mapCaseStatusToBadgeType(status)
  const isChecked = isCheckedCaseStatus(status)

  // Impression = badge visible. On mount here since the badge renders immediately;
  // surfaces that reveal it on interaction pass `trackImpression={false}`. Guard
  // stops re-renders from re-emitting.
  const lastImpressionKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (!surface || !trackImpression) return
    const impressionKey = `${surface}:${badgeType}`
    if (lastImpressionKeyRef.current === impressionKey) return
    lastImpressionKeyRef.current = impressionKey
    trackCitatorBadgeImpression({ trackEvent, surface, badgeType })
  }, [trackEvent, surface, badgeType, trackImpression])

  const handleBadgeClick = () => {
    if (!surface) return
    trackCitatorBadgeClicked({ trackEvent, surface, badgeType })
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={handleBadgeClick}
          className={clsx('inline-flex self-start cursor-pointer focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 rounded', className)}
        >
          {isChecked ? <CaseStatusCheckedLabel label={checkedLabel} size={checkedLabelSize} /> : <CaseStatusTag status={status} />}
        </button>
      </PopoverTrigger>
      <PopoverContent
        width="xl"
        showCloseButton
        reserveCloseButtonSpace={false}
        showArrow
        side="bottom"
        align="center"
        sideOffset={8}
        className="max-w-[calc(100vw-2rem)]"
      >
        <div className="flex flex-col text-brand-neutral-950">
          <div className="flex flex-col gap-1">
            <p className="text-sm font-semibold leading-5 tracking-[-0.15px]">{heading}</p>
            {body.map((paragraph, index) => (
              <p key={index} className="text-xs font-normal leading-4">
                {paragraph}
              </p>
            ))}
          </div>
          {onViewCitationAnalysis && (
            <PopoverClose asChild>
              <button
                type="button"
                onClick={onViewCitationAnalysis}
                className="inline-flex items-center gap-0.5 self-start mt-2 text-xs font-medium text-brand-700 underline hover:text-brand-800 cursor-pointer"
              >
                View citation analysis
                <OpenNew className="w-3.5 h-3.5" />
              </button>
            </PopoverClose>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
